import React, { useEffect, useState } from "react";
import { Switch } from "components/Graphs/Switch";
import { Input } from "components/Graphs/Input";
import { Button } from "components/Graphs/Button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "components/Graphs/Tabs";
import {
  BookOpen,
  Server,
  Globe,
  LockKeyhole,
  Shield,
  Plus,
  Trash2,
  ExternalLink,
  AlertCircle,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/Graphs/Select";
import { FlatCard } from "components/UI-lib";
import { RuntimeType } from "./launchable-creator";
import {
  RadioCardGroup,
  RadioCardIndicator,
  RadioCardItem,
} from "components/Graphs/RadioCardGroup";
import { ContainerSelected } from "components/Environment/shared/BuildTypes";
import {
  ArrowUpRightIcon,
  BookOpenIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { Button as OldButton } from "components/UI-lib";
import yaml from "js-yaml";
import { Tooltip } from "@mui/material";
import { FadeInDiv } from "components/Motion/FadeInDiv";
import { AnimatePresence, motion } from "framer-motion";

export interface NetworkService {
  id: string;
  name: string;
  port: string;
  isJupyter: boolean;
  containerImage?: string;
  hasPortConflict?: boolean;
  hasPortRangeError?: boolean;
  hasInvalidPortError?: boolean;
}

export interface FirewallRule {
  id: string;
  ports: string;
  allowedIPs: string;
  hasPortConflict?: boolean;
  hasInvalidPortError?: boolean;
  hasInvalidFormatError?: boolean;
}

interface NetworkAccessConfigurationProps {
  services: NetworkService[];
  onServicesChange: (services: NetworkService[]) => void;
  jupyterDirectory: string;
  onJupyterDirectoryChange: (directory: string) => void;
  jupyterStatus: "configured" | "not-configured";
  onJupyterStatusChange: (status: "configured" | "not-configured") => void;
  runtimeType: RuntimeType;
  container: ContainerSelected;
  firewallRules?: FirewallRule[];
  onFirewallRulesChange?: (rules: FirewallRule[]) => void;
}

const NetworkAccessConfiguration: React.FC<NetworkAccessConfigurationProps> = ({
  services,
  onServicesChange,
  jupyterDirectory,
  onJupyterDirectoryChange,
  jupyterStatus,
  onJupyterStatusChange,
  runtimeType,
  container,
  firewallRules = [],
  onFirewallRulesChange = () => { },
}) => {
  const [activeTab, setActiveTab] = useState<string>("tunnels");
  const [newRulePort, setNewRulePort] = useState<string>("");
  const [newRuleAllowedIPs, setNewRuleAllowedIPs] = useState<string>("all");
  const [portConflictError, setPortConflictError] = useState<string | null>(
    null
  );
  const [portRangeError, setPortRangeError] = useState<string | null>(null);
  const [firewallValidationError, setFirewallValidationError] = useState<
    string | null
  >(null);

  const [isOpen, setIsOpen] = useState(false);

  // Helper function to validate port number
  const isValidPortNumber = (port: number): boolean => {
    return !isNaN(port) && port >= 1 && port <= 65535;
  };

  // Helper function to validate port format
  const isValidPortFormat = (portStr: string): boolean => {
    if (!portStr.includes("-")) {
      const port = parseInt(portStr.trim());
      return isValidPortNumber(port);
    }
    const parts = portStr.split("-").map((p) => p.trim());
    if (parts.length !== 2) return false;
    const start = parseInt(parts[0]);
    const end = parseInt(parts[1]);
    return isValidPortNumber(start) && isValidPortNumber(end) && start < end;
  };

  // Helper function to parse port range
  const parsePortRange = (portStr: string): [number, number] => {
    if (!portStr.includes("-")) {
      const port = parseInt(portStr.trim());
      return [port, port];
    }
    const [start, end] = portStr.split("-").map((p) => parseInt(p.trim()));
    return [start, end];
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Parse Docker Compose YAML to extract NetworkService entries
  useEffect(() => {
    if (!!container?.dockerCompose) {
      try {
        const composeConfig = yaml.load(
          container.dockerCompose.yamlString
        ) as any;
        const servicesConfig = composeConfig?.services || {};
        console.log("servicesConfig", servicesConfig);

        // Keep track of existing tunnel names to avoid duplicates
        const existingTunnelNames = new Set(services.map((s) => s.name));
        let tunnelCounter = services.length;

        const newServices: NetworkService[] = Object.entries(
          servicesConfig
        ).flatMap(([name, service]: [string, any], index: number) => {
          const image = service.image || "";
          const ports = service.ports || [];
          return ports
            .map(
              (
                port:
                  | string
                  | { published: string | number; target: string | number }
              ) => {
                let hostPort: string;
                if (typeof port === "string") {
                  const parts = port.split(":");
                  // Handle format like "0.0.0.0:8888:8888" where we need the second part
                  if (parts.length === 3) {
                    hostPort = parts[1];
                  } else {
                    // Handle format like "8888:8888" where we take the first part
                    hostPort = parts[0];
                  }
                } else {
                  hostPort = port.published.toString();
                }

                if (!isValidPortNumber(parseInt(hostPort))) {
                  return null;
                }

                // Generate a unique tunnel name
                let tunnelName;
                do {
                  tunnelCounter++;
                  tunnelName = `tunnel-${tunnelCounter}`;
                } while (existingTunnelNames.has(tunnelName));
                existingTunnelNames.add(tunnelName);

                return {
                  id: `${image}:${hostPort}`,
                  name: tunnelName,
                  port: hostPort,
                  isJupyter: false,
                  containerImage: image,
                  hasPortConflict: false,
                  hasPortRangeError: false,
                  hasInvalidPortError: false,
                };
              }
            )
            .filter((service): service is NetworkService => service !== null);
        });

        const existingJupyter = services.find((service) => service.isJupyter);
        const jupyterService: NetworkService[] = [];
        if (!existingJupyter && container.dockerCompose.jupyterInstall) {
          const newService: NetworkService = {
            id: `service-${Date.now()}`,
            name: "jupyter-notebook",
            port: "8888",
            isJupyter: true,
            containerImage: undefined,
          };
          jupyterService.push(newService);
        }

        const mergedServices = [
          ...jupyterService,
          ...services.filter((s) => newServices.every((ns) => ns.id !== s.id)),
          ...newServices,
        ];

        const {
          services: checkedServices,
          firewallRules: checkedRules,
          hasConflict,
          hasRangeError,
          hasInvalidPortError,
        } = checkPortConflicts(mergedServices, firewallRules);

        if (hasConflict) {
          setPortConflictError(
            "Warning: Port conflict detected between services. Please use different ports for your services."
          );
        } else {
          setPortConflictError(null);
        }

        if (hasRangeError) {
          setPortRangeError(
            "Services can only use single port numbers, not port ranges."
          );
        } else {
          setPortRangeError(null);
        }

        if (hasInvalidPortError) {
          setFirewallValidationError(
            "Invalid port numbers detected. Ports must be between 1 and 65535."
          );
        } else {
          setFirewallValidationError(null);
        }

        onServicesChange(checkedServices);
        onFirewallRulesChange(checkedRules);
      } catch (error) {
        console.error("Failed to parse Docker Compose YAML:", error);
        setFirewallValidationError("Invalid Docker Compose YAML format.");
      }
    }
  }, [container]);

  // Check port conflicts
  const checkPortConflicts = (
    allServices: NetworkService[],
    allFirewallRules: FirewallRule[],
    currentItemId?: string
  ): {
    services: NetworkService[];
    firewallRules: FirewallRule[];
    hasConflict: boolean;
    hasRangeError: boolean;
    hasInvalidPortError: boolean;
  } => {
    let hasConflict = false;
    let hasRangeError = false;
    let hasInvalidPortError = false;

    const updatedServices = [...allServices];
    const updatedFirewallRules = [...allFirewallRules];

    updatedServices.forEach((service) => {
      service.hasPortConflict = false;
      service.hasPortRangeError = false;
      service.hasInvalidPortError = false;
    });

    updatedFirewallRules.forEach((rule) => {
      rule.hasPortConflict = false;
      rule.hasInvalidPortError = false;
      rule.hasInvalidFormatError = false;
    });

    updatedFirewallRules.forEach((rule) => {
      if (!isValidPortFormat(rule.ports)) {
        rule.hasInvalidFormatError = true;
        hasInvalidPortError = true;
      }
    });

    if (hasInvalidPortError) {
      return {
        services: updatedServices,
        firewallRules: updatedFirewallRules,
        hasConflict: false,
        hasRangeError: false,
        hasInvalidPortError: true,
      };
    }

    updatedServices.forEach((service) => {
      if (service.port) {
        const portNum = parseInt(service.port);
        if (isNaN(portNum) || !isValidPortNumber(portNum)) {
          service.hasInvalidPortError = true;
          hasInvalidPortError = true;
        }
      }
    });

    updatedServices.forEach((service) => {
      if (service.port && service.port.includes("-")) {
        service.hasPortRangeError = true;
        hasRangeError = true;
      }
    });

    for (let i = 0; i < updatedServices.length; i++) {
      if (!updatedServices[i].port) continue;
      const port = parseInt(updatedServices[i].port);

      for (let j = i + 1; j < updatedServices.length; j++) {
        if (!updatedServices[j].port) continue;
        if (parseInt(updatedServices[j].port) === port) {
          updatedServices[i].hasPortConflict = true;
          updatedServices[j].hasPortConflict = true;
          hasConflict = true;
        }
      }

      for (const rule of updatedFirewallRules) {
        const [start, end] = parsePortRange(rule.ports);
        if (port >= start && port <= end) {
          updatedServices[i].hasPortConflict = true;
          rule.hasPortConflict = true;
          hasConflict = true;
        }
      }
    }

    for (let i = 0; i < updatedFirewallRules.length; i++) {
      const [start1, end1] = parsePortRange(updatedFirewallRules[i].ports);
      for (let j = i + 1; j < updatedFirewallRules.length; j++) {
        const [start2, end2] = parsePortRange(updatedFirewallRules[j].ports);
        if (!(end1 < start2 || end2 < start1)) {
          updatedFirewallRules[i].hasPortConflict = true;
          updatedFirewallRules[j].hasPortConflict = true;
          hasConflict = true;
        }
      }
    }

    return {
      services: updatedServices,
      firewallRules: updatedFirewallRules,
      hasConflict,
      hasRangeError,
      hasInvalidPortError,
    };
  };

  // Add a new service
  const addService = (isJupyter = false) => {
    let name = isJupyter ? "jupyter-notebook" : `tunnel-${services.length + 1}`;

    // Check if the name already exists and find the next available number
    if (services.find((service) => service.name === name)) {
      // Find the next available number
      let counter = services.length;
      let newName;
      do {
        counter++;
        newName = `tunnel-${counter}`;
      } while (services.find((service) => service.name === newName));
      name = newName;
    }

    const newService: NetworkService = {
      id: `service-${Date.now()}`,
      name,
      port: isJupyter ? "8888" : "",
      isJupyter,
      containerImage: undefined,
    };

    const updatedServices = [...services, newService];
    const {
      services: checkedServices,
      firewallRules: checkedRules,
      hasConflict,
      hasRangeError,
    } = checkPortConflicts(updatedServices, firewallRules, newService.id);

    if (hasConflict) {
      setPortConflictError(
        "Warning: Port conflict detected between services. Please use different ports for your services."
      );
    } else {
      setPortConflictError(null);
    }

    if (hasRangeError) {
      setPortRangeError(
        "Services can only use single port numbers, not port ranges."
      );
    } else {
      setPortRangeError(null);
    }

    onServicesChange(checkedServices);
    onFirewallRulesChange(checkedRules);
  };

  // Remove a service
  const removeService = (id: string) => {
    const updatedServices = services.filter((service) => service.id !== id);
    const {
      services: checkedServices,
      firewallRules: checkedRules,
      hasConflict,
      hasRangeError,
    } = checkPortConflicts(updatedServices, firewallRules);

    if (hasConflict) {
      setPortConflictError(
        "Warning: Port conflict detected between services. Please use different ports for your services."
      );
    } else {
      setPortConflictError(null);
    }

    if (hasRangeError) {
      setPortRangeError(
        "Services can only use single port numbers, not port ranges."
      );
    } else {
      setPortRangeError(null);
    }

    onServicesChange(checkedServices);
    onFirewallRulesChange(checkedRules);
  };

  // Update a service
  const updateService = (id: string, updates: Partial<NetworkService>) => {
    const updatedServices = services.map((service) =>
      service.id === id ? { ...service, ...updates } : service
    );

    const {
      services: checkedServices,
      firewallRules: checkedRules,
      hasConflict,
      hasRangeError,
    } = checkPortConflicts(updatedServices, firewallRules, id);

    if (hasConflict) {
      setPortConflictError(
        "Warning: Port conflict detected between services. Please use different ports for your services."
      );
    } else {
      setPortConflictError(null);
    }

    if (hasRangeError) {
      setPortRangeError(
        "Services can only use single port numbers, not port ranges."
      );
    } else {
      setPortRangeError(null);
    }

    onServicesChange(checkedServices);
    onFirewallRulesChange(checkedRules);
  };

  // Add a new firewall rule
  const addFirewallRule = () => {
    if (!newRulePort.trim()) {
      return;
    }

    if (!isValidPortFormat(newRulePort)) {
      if (newRulePort.includes("-")) {
        const parts = newRulePort.split("-").map((p) => p.trim());
        if (parts.length !== 2) {
          setFirewallValidationError(
            "Invalid range format. Use format: 8000-8100"
          );
          return;
        }

        const start = parseInt(parts[0]);
        const end = parseInt(parts[1]);

        if (isNaN(start) || isNaN(end)) {
          setFirewallValidationError("Ports must be valid numbers");
          return;
        }

        if (!isValidPortNumber(start) || !isValidPortNumber(end)) {
          setFirewallValidationError("Ports must be between 1 and 65535");
          return;
        }

        if (start >= end) {
          setFirewallValidationError("Start port must be less than end port");
          return;
        }
      } else {
        const port = parseInt(newRulePort);
        if (isNaN(port)) {
          setFirewallValidationError("Port must be a valid number");
          return;
        }
        if (!isValidPortNumber(port)) {
          setFirewallValidationError("Port must be between 1 and 65535");
          return;
        }
      }
    }

    const newRule: FirewallRule = {
      id: `rule-${Date.now()}`,
      ports: newRulePort,
      allowedIPs: newRuleAllowedIPs,
    };

    const updatedRules = [...firewallRules, newRule];
    const {
      services: checkedServices,
      firewallRules: checkedRules,
      hasConflict,
    } = checkPortConflicts(services, updatedRules, newRule.id);

    const newRuleWithValidation = checkedRules.find((r) => r.id === newRule.id);
    if (newRuleWithValidation?.hasPortConflict) {
      const conflictingRules = checkedRules.filter(
        (r) => r.id !== newRule.id && r.hasPortConflict
      );
      const conflictingServices = checkedServices.filter(
        (s) => s.hasPortConflict
      );

      if (conflictingRules.length > 0) {
        setFirewallValidationError(
          `Port${newRulePort.includes("-") ? " range" : ""
          } conflicts with existing rule${conflictingRules.length > 1 ? "s" : ""
          }: ${conflictingRules.map((r) => r.ports).join(", ")}`
        );
        return;
      }

      if (conflictingServices.length > 0) {
        setFirewallValidationError(
          `Port${newRulePort.includes("-") ? " range" : ""
          } conflicts with service${conflictingServices.length > 1 ? "s" : ""
          }: ${conflictingServices
            .map((s) => `${s.name} (port ${s.port})`)
            .join(", ")}`
        );
        return;
      }
    }

    setFirewallValidationError(null);
    onServicesChange(checkedServices);
    onFirewallRulesChange(checkedRules);
    setNewRulePort("");
    setNewRuleAllowedIPs("all");
  };

  // Remove a firewall rule
  const removeFirewallRule = (id: string) => {
    const updatedRules = firewallRules.filter((rule) => rule.id !== id);
    const {
      services: checkedServices,
      firewallRules: checkedRules,
      hasConflict,
      hasRangeError,
    } = checkPortConflicts(services, updatedRules);

    if (hasConflict) {
      setPortConflictError(
        "Warning: Port conflict detected. Please use different ports."
      );
    } else {
      setPortConflictError(null);
    }

    if (hasRangeError) {
      setPortRangeError(
        "Services can only use single port numbers, not port ranges."
      );
    } else {
      setPortRangeError(null);
    }

    onServicesChange(checkedServices);
    onFirewallRulesChange(checkedRules);
  };

  // Toggle Jupyter and manage the Jupyter service
  const handleJupyterToggle = (enabled: boolean) => {
    if (enabled) {
      console.log("services", services);
      const existingJupyter = services.find((service) => service.isJupyter);
      if (!existingJupyter) {
        addService(true);
      }
    } else {
      const updatedServices = services.filter((service) => !service.isJupyter);
      const {
        services: checkedServices,
        firewallRules: checkedRules,
        hasConflict,
        hasRangeError,
      } = checkPortConflicts(updatedServices, firewallRules);

      if (hasConflict) {
        setPortConflictError(
          "Warning: Port conflict detected between services. Please use different ports for your services."
        );
      } else {
        setPortConflictError(null);
      }

      if (hasRangeError) {
        setPortRangeError(
          "Services can only use single port numbers, not port ranges."
        );
      } else {
        setPortRangeError(null);
      }
      console.log("checkedServices", checkedServices);
      onServicesChange(checkedServices);
      onFirewallRulesChange(checkedRules);
    }
  };

  return (
    <div>
      {runtimeType === "VM" ||
        (runtimeType === "container" && !!container.dockerCompose) ? (
        <div>
          <div className="flex flex-row items-end mb-4">
            <h2 className="text-xl font-semibold text-primary">
              Do you want a Jupyter Notebook experience?
            </h2>
          </div>
          <FlatCard className="mb-8">
            <div className="">
              <div className="flex flex-col">
                <RadioCardGroup
                  value={jupyterStatus}
                  onValueChange={(value) => {
                    onJupyterStatusChange(
                      value as "configured" | "not-configured"
                    );
                    handleJupyterToggle(value === "configured");
                  }}
                  className="grid grid-cols-1 md:grid-cols-2 gap-4"
                >
                  <RadioCardItem value="configured">
                    <div className="flex items-start gap-3">
                      <RadioCardIndicator className="mt-1" />
                      <div>
                        <span className="sm:text-sm text-primary">
                          Yes, Install Jupyter on the <strong>Host</strong>{" "}
                          (Recommended)
                        </span>
                      </div>
                    </div>
                  </RadioCardItem>
                  <RadioCardItem value="not-configured">
                    <div className="flex items-start gap-3">
                      <RadioCardIndicator className="mt-1" />
                      <div>
                        <span className="sm:text-sm text-primary">
                          No, I don't want Jupyter (Not Recommended)
                        </span>
                      </div>
                    </div>
                  </RadioCardItem>
                </RadioCardGroup>

                <motion.div
                  className="p-2 bg-secondaryBg rounded-md text-sm text-primary cursor-pointer flex flex-col mt-2"
                  initial={false}
                >
                  <div
                    className="flex items-center group cursor-pointer"
                    onClick={() => setIsOpen(!isOpen)}
                  >
                    <p className="font-medium text-secondary mr-2 group-hover:text-highlight">
                      What does this mean?
                    </p>
                    <motion.div
                      animate={{ rotate: isOpen ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ChevronDownIcon className="w-5 h-5 text-secondary group-hover:text-highlight" />
                    </motion.div>
                  </div>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.ul
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="list-disc pl-5 text-secondary mt-2"
                      >
                        <div className="flex flex-row gap-2 justify-between">
                          <div className="flex flex-col justify-center items-center p-3 rounded-md bg-secondaryBg">
                            <h2 className="text-sm font-semibold text-white mb-1">
                              Jupter Experience
                            </h2>
                            <div className="text-sm text-white mb-2">
                              What the end user will have:
                            </div>
                            <FadeInDiv
                              className={`flex flex-row items-center p-4 border-2 border-zinc-800 rounded-md bg-[url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABsAAAAPCAQAAAC/mnxTAAAAIUlEQVR4AWPc7sywl4GBgUSScTsD6YBsbYPNkaOOHHUkALSqN8mc6mmTAAAAAElFTkSuQmCC")]`}
                            >
                              <OldButton
                                type="primary"
                                label={"Access Notebook"}
                                className="opacity-75"
                                onClick={() => { }}
                              />
                            </FadeInDiv>
                            <span className="text-xs text-highlight mt-6">
                              <strong>One click access</strong> to your content
                              on the instance.
                            </span>
                          </div>

                          <div className="flex flex-col justify-center items-center p-3 rounded-md bg-secondaryBg">
                            <h2 className="text-sm font-semibold text-white mb-1">
                              No Jupyter Experience
                            </h2>
                            <div className="text-sm text-white mb-2">
                              What the end user will have:
                            </div>
                            <FadeInDiv
                              className={`flex flex-row items-center p-4 border-2 border-zinc-800 rounded-md bg-[url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABsAAAAPCAQAAAC/mnxTAAAAIUlEQVR4AWPc7sywl4GBgUSScTsD6YBsbYPNkaOOHHUkALSqN8mc6mmTAAAAAElFTkSuQmCC")]`}
                            >
                              <OldButton
                                type="primary"
                                label={"Access GPU"}
                                className="opacity-75"
                                onClick={() => { }}
                              />
                            </FadeInDiv>
                            <span className="text-xs text-amber-500 mt-6">
                              Will access your GPU through{" "}
                              <strong>SSH using the Brev CLI</strong> (Not
                              recommended)
                            </span>
                          </div>
                        </div>
                      </motion.ul>
                    )}
                  </AnimatePresence>
                </motion.div>
              </div>
            </div>
          </FlatCard>
        </div>
      ) : (
        <FlatCard className="mb-8">
          <div className="pb-2">
            <h3 className="text-xl flex items-center text-primary font-semibold">
              <BookOpenIcon className="mr-2 h-5 w-5" />
              Preconfigured Jupyter Experience
            </h3>
            <ul className="list-disc pl-5">
              <li className="text-secondary text-sm">
                Your featured container comes with Jupyter running in the{" "}
                <strong>container</strong> on port 8888.
              </li>
              <li className="text-secondary text-sm">
                We will automatically configure a tunnel so the end user can
                access Jupyter.
              </li>
            </ul>
          </div>
          <motion.div
            className="p-2 bg-secondaryBg rounded-md text-sm text-primary cursor-pointer flex flex-col"
            initial={false}
          >
            <div
              className="flex items-center group cursor-pointer"
              onClick={() => setIsOpen(!isOpen)}
            >
              <p className="font-medium text-secondary mr-2 group-hover:text-highlight">
                What does this mean?
              </p>
              <motion.div
                animate={{ rotate: isOpen ? 180 : 0 }}
                transition={{ duration: 0.2 }}
              >
                <ChevronDownIcon className="w-5 h-5 text-secondary group-hover:text-highlight" />
              </motion.div>
            </div>

            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.ul
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="list-disc pl-5 text-secondary mt-2"
                >
                  <div className="flex flex-row gap-2 justify-center">
                    <div className="flex flex-col justify-center items-center p-3 rounded-md bg-secondaryBg">
                      <h2 className="text-sm font-semibold text-white mb-1">
                        Jupter Experience
                      </h2>
                      <div className="text-sm text-white mb-2">
                        What the end user will have:
                      </div>
                      <FadeInDiv
                        className={`flex flex-row items-center p-4 border-2 border-zinc-800 rounded-md bg-[url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABsAAAAPCAQAAAC/mnxTAAAAIUlEQVR4AWPc7sywl4GBgUSScTsD6YBsbYPNkaOOHHUkALSqN8mc6mmTAAAAAElFTkSuQmCC")]`}
                      >
                        <OldButton
                          type="primary"
                          label={"Access Notebook"}
                          className="opacity-75"
                          onClick={() => { }}
                        />
                      </FadeInDiv>
                      <span className="text-xs text-highlight mt-6">
                        <strong>One click access</strong> to your content on the
                        instance.
                      </span>
                    </div>
                  </div>
                </motion.ul>
              )}
            </AnimatePresence>
          </motion.div>
          <div className="pt-4"></div>
        </FlatCard>
      )}
      <Tooltip
        title={
          !!container.verbBuild
            ? "Feature Containers don't support additional Secure Links or TCP/UDP Ports on creation."
            : ""
        }
        followCursor
        placement="top"
      >
        <div className="relative h-full">
          <div className={!!container.verbBuild ? "hidden" : ""}>
            <div className="mb-6">
              <h3 className="text-xl font-semibold text-primary">
                Do you need to expose services to the internet?
              </h3>
              <p className="text-secondary text-sm">
                Use a Secure Link to protect a http service with login, or expose tcp/udp ports.
              </p>
            </div>
            <Tabs
              defaultValue="tunnels"
              className="mb-6"
              onValueChange={setActiveTab}
            >
              <TabsList className="grid w-full grid-cols-2" variant="solid">
                <TabsTrigger
                  value="tunnels"
                  className="flex items-center"
                  disabled={!!container.verbBuild}
                >
                  <LockKeyhole className="mr-2 h-4 w-4" /> Secure Link
                </TabsTrigger>
                <TabsTrigger
                  value="firewall"
                  className="flex items-center"
                  disabled={!!container.verbBuild}
                >
                  <Globe className="mr-2 h-4 w-4" /> TCP/UDP Ports
                </TabsTrigger>
              </TabsList>
              <TabsContent value="tunnels" className="pt-4 space-y-6">
                {services.filter((service) => service.isJupyter).length > 0 && (
                  <FlatCard className="relative">
                    <div className="">
                      <div className="flex justify-between items-center">
                        <h2 className="text-base flex items-center flex-1">
                          <Server className="mr-2 h-5 w-5 text-highlight" />
                          Jupyter Notebook
                        </h2>
                        <div className="flex justify-between items-center flex-1">
                          <span>Port: 8888</span>
                          <span className="inline-flex items-center gap-x-1 whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset bg-gray-50 text-gray-900 ring-gray-500/30 dark:bg-gray-400/10 dark:text-gray-400 dark:ring-gray-400/20">
                            Created from Jupyter Selection
                          </span>
                        </div>
                      </div>
                    </div>
                  </FlatCard>
                )}
                {services.filter((s) => !s.isJupyter).length === 0 ? (
                  <div className="text-center py-8 border-2 border-dashed border-zinc-800 rounded-lg">
                    <LockKeyhole className="mx-auto h-12 w-12 text-gray-400 mb-3" />
                    <h3 className="text-lg font-medium text-gray-900 mb-1">
                      No additional secure links configured
                    </h3>
                    <p className="text-secondary mb-4">
                      Add secure links to expose http services with login
                    </p>
                    <Button
                      onClick={() => addService()}
                      disabled={!!container.verbBuild}
                    >
                      <Plus className="mr-2 h-4 w-4" /> Add Secure Link
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {services
                      .filter((s) => !s.isJupyter)
                      .map((service) => (
                        <FlatCard key={service.id} className="relative">
                          <div className="pb-2">
                            <div className="flex justify-between items-center">
                              <h2 className="text-base flex items-center">
                                <Server className="mr-2 h-5 w-5 text-highlight" />
                                {service.name}
                                {service.hasPortConflict && (
                                  <span className="ml-2 text-xs bg-red-500 text-white px-2 py-1 rounded-full">
                                    Port Conflict
                                  </span>
                                )}
                                {service.hasPortRangeError && (
                                  <span className="ml-2 text-xs bg-red-500 text-white px-2 py-1 rounded-full">
                                    Invalid Port
                                  </span>
                                )}
                                {service.hasInvalidPortError && (
                                  <span className="ml-2 text-xs bg-red-500 text-white px-2 py-1 rounded-full">
                                    Invalid Port Number
                                  </span>
                                )}
                              </h2>
                              <div className="flex items-center">
                                {service.containerImage && (
                                  <div className="flex items-center">
                                    <Tooltip
                                      placement="top"
                                      title={`We automatically generated this secure link based on port configurations within the container image ${service.containerImage}. If you don't need the secure link, you can remove it.`}
                                    >
                                      <span className="inline-flex items-center gap-x-1 whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset bg-gray-50 text-gray-900 ring-gray-500/30 dark:bg-gray-400/10 dark:text-gray-400 dark:ring-gray-400/20 cursor-pointer">
                                        Auto Generated from Port Configurations
                                      </span>
                                    </Tooltip>
                                  </div>
                                )}
                                <Button
                                  variant="ghost"
                                  onClick={() => removeService(service.id)}
                                  disabled={!!container.verbBuild}
                                >
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              </div>
                            </div>
                          </div>
                          <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <label
                                  htmlFor={`service-name-${service.id}`}
                                  className="text-sm"
                                >
                                  Secure Link Name
                                </label>
                                <Input
                                  id={`service-name-${service.id}`}
                                  value={service.name}
                                  onChange={(e) =>
                                    updateService(service.id, {
                                      name: e.target.value,
                                    })
                                  }
                                />
                              </div>
                              <div className="space-y-2">
                                <label htmlFor={`service-port-${service.id}`}>
                                  Port
                                </label>
                                <Input
                                  id={`service-port-${service.id}`}
                                  value={service.port}
                                  onChange={(e) =>
                                    updateService(service.id, {
                                      port: e.target.value,
                                    })
                                  }
                                  placeholder="e.g., 8080"
                                  className={
                                    service.hasPortConflict ||
                                      service.hasPortRangeError ||
                                      service.hasInvalidPortError
                                      ? "border-yellow-400"
                                      : ""
                                  }
                                />
                                {service.hasPortConflict && (
                                  <p className="text-xs text-yellow-700">
                                    This port conflicts with another service
                                  </p>
                                )}
                                {service.hasPortRangeError && (
                                  <p className="text-xs text-yellow-700">
                                    Services can only use single port numbers,
                                    not ranges
                                  </p>
                                )}
                                {service.hasInvalidPortError && (
                                  <p className="text-xs text-yellow-700">
                                    Please enter a valid port number between 1
                                    and 65535
                                  </p>
                                )}
                                <p className="text-xs text-secondary">
                                  Enter a single port number (ranges like
                                  8000-8100 are not allowed for secure links)
                                </p>
                              </div>
                            </div>
                          </div>
                        </FlatCard>
                      ))}
                    {services.filter((s) => !s.isJupyter).length > 0 && (
                      <div className="flex justify-center items-center mb-4">
                        <Button
                          onClick={() => addService()}
                          variant="primary"
                          className="flex items-center"
                          disabled={!!container.verbBuild}
                        >
                          <Plus className="mr-2 h-4 w-4" /> Add Secure Link
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </TabsContent>
              <TabsContent value="firewall" className="pt-4">
                <FlatCard>
                  <div className="pb-2">
                    <p className="text-sm text-primary">
                      Port(s) (range allowed)
                    </p>
                  </div>
                  <div className="flex flex-col w-full">
                    <div className="flex items-start gap-2 max-w-[550px]">
                      <div className="flex flex-col w-full items-start">
                        <Input
                          placeholder="Port or port range (e.g., 8000-8100)"
                          className="flex-1"
                          value={newRulePort}
                          onChange={(e) => setNewRulePort(e.target.value)}
                        />
                      </div>
                      <div className="flex items-center">
                        <Select
                          value={newRuleAllowedIPs}
                          onValueChange={(value) => setNewRuleAllowedIPs(value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Allow All IPs" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Allow All IPs</SelectItem>
                            <SelectItem value="user-ip">
                              Allow Only User's IP
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        variant="primary"
                        onClick={addFirewallRule}
                        disabled={!!container.verbBuild}
                      >
                        <Plus className="h-4 w-4" /> Add Rule
                      </Button>
                    </div>
                    {firewallValidationError && (
                      <p className="text-xs text-red-500 mt-2">
                        {firewallValidationError}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col w-full mt-2">
                    <div className="flex items-center w-full">
                      <div className="flex flex-col w-full space-y-4">
                        <div className="flex flex-col w-full pt-4">
                          <div className="space-y-4">
                            {firewallRules.map((rule) => (
                              <div
                                key={rule.id}
                                className="flex items-center gap-2 p-3 rounded-md border border-zinc-800"
                              >
                                <div className="flex-1 flex items-center">
                                  <Server className="mr-2 h-5 w-5 text-highlight" />
                                  <p className="text-sm font-medium">
                                    {rule.ports}
                                    {rule.allowedIPs === "0.0.0.0/0" && (
                                      <span className="inline-flex items-center gap-x-1 whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset bg-gray-50 text-gray-900 ring-gray-500/30 dark:bg-gray-400/10 dark:text-gray-400 dark:ring-gray-400/20 ml-2">
                                        Allow All IPs
                                      </span>
                                    )}
                                    {rule.allowedIPs === "user-ip" && (
                                      <span className="inline-flex items-center gap-x-1 whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset bg-gray-50 text-gray-900 ring-gray-500/30 dark:bg-gray-400/10 dark:text-gray-400 dark:ring-gray-400/20 ml-2">
                                        Allow Only User's IP
                                      </span>
                                    )}
                                    {rule.hasPortConflict && (
                                      <span className="ml-2 text-xs bg-yellow-200 text-yellow-800 px-2 py-0.5 rounded-full">
                                        Conflict
                                      </span>
                                    )}
                                    {rule.hasInvalidFormatError && (
                                      <span className="ml-2 text-xs bg-red-500 text-white px-2 py-0.5 rounded-full">
                                        Invalid Format
                                      </span>
                                    )}
                                  </p>
                                </div>
                                <Button
                                  variant="ghost"
                                  onClick={() => removeFirewallRule(rule.id)}
                                  disabled={!!container.verbBuild}
                                >
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </FlatCard>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </Tooltip>
    </div>
  );
};

export default NetworkAccessConfiguration;
